import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { Select } from '../../components/common/Select';
import { adminService } from '../../services/adminService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import { WITHDRAWAL_STATUS_COLORS } from '../../utils/constants';
import toast from 'react-hot-toast';

export const AdminWithdrawals: React.FC = () => {
  const { user } = useAuth();
  const isAuditor = user?.role === 'AUDITOR';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'withdrawals', { page, limit: 20, status: status || undefined }],
    queryFn: () => adminService.getWithdrawals({ page, limit: 20, status: status || undefined }),
  });

  const finalizeMutation = useMutation({
    mutationFn: (id: string) => adminService.finalizeWithdrawal(id),
    onSuccess: () => {
      setProcessingId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'withdrawals'] });
      toast.success('Transfer finalized with Paystack');
    },
    onError: (e: any) => { setProcessingId(null); toast.error(e?.response?.data?.message || 'Failed to finalize transfer'); },
  });

  if (isLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  const withdrawals = data?.data?.withdrawals || [];
  const pagination = data?.data?.pagination;

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Withdrawals</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Review and approve withdrawal requests</p>
      </div>
      <div className="mb-6 w-64">
        <Select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          options={[
            { value: '', label: 'All Status' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'PROCESSING', label: 'Processing' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'FAILED', label: 'Failed' },
          ]}
        />
      </div>
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fee</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Net</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent</th>
                <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                {!isAuditor && <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {withdrawals.map((w: any) => (
                <tr key={w.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{w.wallet?.user?.firstName} {w.wallet?.user?.lastName}</p>
                    <p className="text-gray-400 dark:text-gray-500 text-xs">{w.wallet?.user?.email}</p>
                  </td>
                  <td className="px-6 py-4 font-medium dark:text-gray-200">{formatCurrency(w.amount, w.currency || 'USD')}</td>
                  <td className="px-6 py-4 text-gray-500 dark:text-gray-400">{formatCurrency(w.fee, w.currency || 'USD')}</td>
                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(w.netAmount, w.currency || 'USD')}</td>
                  <td className="px-6 py-4 text-gray-500 dark:text-gray-400">{w.method}</td>
                  <td className="px-6 py-4"><Badge variant={WITHDRAWAL_STATUS_COLORS[w.status as keyof typeof WITHDRAWAL_STATUS_COLORS] || 'default'}>{w.status}</Badge></td>
                  <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400 max-w-48">
                    {(() => {
                      try {
                        const comment = w.comment ? JSON.parse(w.comment) : null;
                        if (w.status === 'FAILED' && comment?.failureReason) return <span className="text-red-600 dark:text-red-400">{comment.failureReason}</span>;
                        if (w.status === 'FAILED' && typeof w.comment === 'string' && !w.comment.startsWith('{')) return <span className="text-red-600 dark:text-red-400">{w.comment}</span>;
                        if (w.status === 'COMPLETED' && comment?.transferCode) return <span>Ref: {comment.transferCode}</span>;
                        if (w.status === 'PROCESSING' && comment?.transferCode) return <span>Ref: {comment.transferCode}</span>;
                        return '—';
                      } catch { return w.comment ? <span className={w.status === 'FAILED' ? 'text-red-600 dark:text-red-400' : ''}>{w.comment}</span> : '—'; }
                    })()}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {w.agentInfo ? (
                      <a href={`/admin/agents/${w.agentInfo.id}`} className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 font-medium">{w.agentInfo.name}</a>
                    ) : <span className="text-gray-400 dark:text-gray-600">—</span>}
                  </td>
                  <td className="px-6 py-4 text-gray-500 dark:text-gray-400">{formatDate(w.createdAt)}</td>
                  {!isAuditor && (
                    <td className="px-6 py-4">
                      {(w.status === 'PENDING' || w.status === 'PROCESSING') && (
                        <Button size="sm" onClick={() => { setProcessingId(w.id); finalizeMutation.mutate(w.id); }} isLoading={processingId === w.id && finalizeMutation.isPending}>
                          {w.status === 'PENDING' ? 'Approve & Send' : 'Retry'}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {withdrawals.length === 0 && (
                <tr><td colSpan={isAuditor ? 9 : 10} className="px-6 py-12 text-center text-gray-400 dark:text-gray-500">No withdrawals found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-8">
          <Button variant="outline" disabled={page === 1} onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Previous</Button>
          <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {pagination.pages}</span>
          <Button variant="outline" disabled={page === pagination.pages} onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Next</Button>
        </div>
      )}
    </AdminLayout>
  );
};
